'use client'

import { cn } from '@/lib/utils'
import { Phone, Mail, Globe, CalendarCheck } from 'lucide-react'
import { useTranslations } from 'next-intl'

interface ContactInfoBlockContent {
  phone?: string
  email?: string
  inquiryUrl?: string
  bookingUrl?: string
}

interface ContactInfoBlockProps {
  content: ContactInfoBlockContent
  className?: string
}

export function ContactInfoBlock({ content, className }: ContactInfoBlockProps) {
  const t = useTranslations('venue')
  const hasContent = content.phone || content.email || content.inquiryUrl || content.bookingUrl
  if (!hasContent) return null

  return (
    <div className={cn('space-y-3', className)}>
      <h3 className="text-lg font-semibold text-white">{t('contact')}</h3>
      <div className="space-y-2">
        {content.phone && (
          <a
            href={`tel:${content.phone}`}
            className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/30 border border-zinc-700/50 hover:border-violet-500/30 transition-colors group"
          >
            <Phone className="h-4 w-4 text-zinc-400 group-hover:text-violet-400 transition-colors" />
            <span className="text-zinc-300 text-sm group-hover:text-white transition-colors">{content.phone}</span>
          </a>
        )}
        {content.email && (
          <a
            href={`mailto:${content.email}`}
            className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/30 border border-zinc-700/50 hover:border-violet-500/30 transition-colors group"
          >
            <Mail className="h-4 w-4 text-zinc-400 group-hover:text-violet-400 transition-colors" />
            <span className="text-zinc-300 text-sm group-hover:text-white transition-colors">{content.email}</span>
          </a>
        )}
        {content.inquiryUrl && (
          <a
            href={content.inquiryUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/30 border border-zinc-700/50 hover:border-violet-500/30 transition-colors group"
          >
            <Globe className="h-4 w-4 text-zinc-400 group-hover:text-violet-400 transition-colors" />
            <span className="text-zinc-300 text-sm group-hover:text-white transition-colors">{t('generalInquiries')}</span>
          </a>
        )}
        {content.bookingUrl && (
          <a
            href={content.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/30 border border-zinc-700/50 hover:border-violet-500/30 transition-colors group"
          >
            <CalendarCheck className="h-4 w-4 text-zinc-400 group-hover:text-violet-400 transition-colors" />
            <span className="text-zinc-300 text-sm group-hover:text-white transition-colors">{t('makeReservation')}</span>
          </a>
        )}
      </div>
    </div>
  )
}
